import { useSearch } from "@tanstack/react-router";
import { Button, Col, Flex, Input, Row, Switch } from "antd";
import Title from "antd/es/typography/Title";
import { IntegrationTestDrawer } from "components/Drawers/IntegrationTestDrawer/IntegrationTestDrawer.tsx";
import { InformationFormData } from "components/Forms/ApiToolGeneralForm/formSchema";
import { DrawerLink } from "components/UI/DrawerLink";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { AdvertiserSelectSearchable } from "components/UI/Selects/AdvertiserSelectSearchable/AdvertiserSelectSearchable.tsx";
import { CategorySelectSearchable } from "components/UI/Selects/CategorySelectSearchable/CategorySelectSearchable.tsx";
import { CountrySelectSearchable } from "components/UI/Selects/CountrySelectSearchable/CountrySelectSearchable.tsx";
import { FormStructureSelectSearchable } from "components/UI/Selects/FormStructureSelectSearchable/FormStructureSelectSearchable.tsx";
import { IntegrationStatusSelectSearchable } from "components/UI/Selects/IntegrationStatusSelectSearchable/IntegrationStatusSelectSearchable.tsx";
import { LabelSelectSearchable } from "components/UI/Selects/LabelSelectSearchable/LabelSelectSearchable.tsx";
import { OfferSelectSearchable } from "components/UI/Selects/OfferSelectSearchable/OfferSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useEffect } from "react";
import { FormItem } from "react-hook-form-antd";
import { PermissionEnum } from "types/enums";
import { DrawerId } from "types/models";

import IntegrationData = App.Data.IntegrationData;

interface IntegrationGeneralFormSectionProps {
    integration: IntegrationData;
    exportPermission: PermissionEnum;
}
interface SearchParams {
    [`integrationTest-id`]: string;
}

const transformIntegrationData = (integration: IntegrationData): InformationFormData => {
    const integrationData = { ...integration };
    delete integrationData.formFields;

    return {
        ...integrationData,
        offers: integration.offers?.map((offer) => offer.id) || [],
        category: integration.category?.id ?? null,
        label_ids: integration.labels?.map((label) => label.id) || [],
        // Default to false until BE adds the field
        use_new_connection_creator:
            (integration as IntegrationData & { use_new_connection_creator?: boolean }).use_new_connection_creator ??
            false,
    };
};

export const IntegrationGeneralFormSection = ({
    integration,
    exportPermission,
}: IntegrationGeneralFormSectionProps) => {
    const { t } = usePaldockTranslation("forms.integration-general");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tC } = usePaldockTranslation("common");
    const generalFormUtils = useGeneralForm<InformationFormData>();
    const { control, fieldNames, reset } = generalFormUtils;
    const searchParams = useSearch({ strict: false }) as SearchParams;

    useEffect(() => {
        if (integration) {
            reset(transformIntegrationData(integration));
        }
    }, [integration, reset]);

    const modifyIntegrationDrawerId = DrawerId.ModifyIntegration;

    return (
        <>
            <Flex justify="space-between" className="mb-5">
                <Title level={3} className="mb-6">
                    {tC("general")}
                </Title>
                {exportPermission && (
                    <DrawerLink
                        drawerId={modifyIntegrationDrawerId}
                        search={{
                            [`${modifyIntegrationDrawerId}-id`]: integration?.id ?? "",
                        }}
                        action="show"
                    >
                        <Button type="primary">{"</>"}</Button>
                    </DrawerLink>
                )}
            </Flex>
            <Row gutter={16}>
                <Col span={8}>
                    <FormItem
                        control={control}
                        name={fieldNames.name?.name || "name"}
                        label={tFields("name.label")}
                        required
                    >
                        <Input placeholder={tFields("name.placeholder")} />
                    </FormItem>
                </Col>
                <Col span={8}>
                    <FormItem
                        control={control}
                        name={fieldNames.category?.name || "category"}
                        label={tFields("category.label")}
                    >
                        <CategorySelectSearchable allowClear />
                    </FormItem>
                </Col>
                <Col span={8}>
                    <FormItem
                        control={control}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore - doesnt exist
                        name={fieldNames.country_id?.name || "country_id"}
                        label={tFields("country.label")}
                    >
                        <CountrySelectSearchable allowClear={true} />
                    </FormItem>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={8}>
                    <FormItem
                        control={control}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore - doesnt exist
                        name={fieldNames.product?.name || "product"}
                        label={t("fields.product.label")}
                    >
                        <Input placeholder={t("fields.product.placeholder")} />
                    </FormItem>
                </Col>
                <Col span={8}>
                    <FormItem
                        control={control}
                        name={fieldNames.status?.name || "status"}
                        label={tFields("status.label")}
                        required
                    >
                        <IntegrationStatusSelectSearchable />
                    </FormItem>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.form_structure_id?.name || "form_structure_id"}
                        label={tFields("form_structure.label")}
                    >
                        <FormStructureSelectSearchable />
                    </FormItem>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.owner_id?.name || "owner_id"}
                        label={t("fields.owner_id.label")}
                    >
                        <AdvertiserSelectSearchable />
                    </FormItem>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <FormItem
                        control={control}
                        name={fieldNames.offers?.name || "offers"}
                        label={tFields("offers.label")}
                    >
                        <OfferSelectSearchable mode="multiple" />
                    </FormItem>
                </Col>
                <Col span={12}>
                    <FormItem control={control} name="label_ids" label={tFields("label.label")}>
                        <LabelSelectSearchable mode="multiple" allowClear />
                    </FormItem>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <FormItem
                        control={control}
                        name="use_new_connection_creator"
                        label={t("fields.use_new_connection_creator.label")}
                        valuePropName="checked"
                    >
                        <Switch />
                    </FormItem>
                </Col>
            </Row>

            {/*<Row gutter={16}>
                <Col span={12}>
                    <FormItem
                        control={control}
                        name={fieldNames.debug_mode?.name || "debug_mode"}
                        label={t("form_fields.enable_test_mode_logging")}
                        valuePropName="checked"
                    >
                        <Switch />
                    </FormItem>
                </Col>
            </Row>*/}

            {/* Hide Log and Run buttons when using new connection creator */}
            {!(integration as IntegrationData & { use_new_connection_creator?: boolean })
                .use_new_connection_creator && (
                <>
                    <Row gutter={8} className="mt-3">
                        <Col span={6}>
                            <div className={"flex items-center gap-x-3"}>
                                <Button type="primary" size={"middle"}>
                                    {t("buttons.log")}
                                </Button>
                                <DrawerLink
                                    drawerId={DrawerId.IntegrationTest}
                                    search={{
                                        [`${DrawerId.IntegrationTest}-id`]: integration.id ?? "",
                                    }}
                                    action="show"
                                >
                                    <Button type="primary" size={"middle"}>
                                        {t("buttons.run")}
                                    </Button>
                                </DrawerLink>
                            </div>
                        </Col>
                    </Row>
                    {searchParams[`${DrawerId.IntegrationTest}-id`] && <IntegrationTestDrawer />}
                </>
            )}
        </>
    );
};
